/**
 * Card step (AGENTS.md 14b): the step's questions as standard fields with one
 * "Done, let's continue" button. Each field is still a node; answers are saved
 * one by one through the normal answer path, so the server sees no difference.
 */
import { useMemo, useState } from "preact/hooks";
import type { FlatNode } from "@wapadesk/engine";
import { cardNodes, validateAnswer } from "@wapadesk/engine";
import type { FormSchema } from "@wapadesk/schema";
import { t } from "@wapadesk/i18n";

export interface CardInputProps {
  form: FormSchema;
  stepId: string;
  stepTitle: string;
  locale: string;
  answers: Record<string, unknown>;
  busy?: boolean;
  /** Called with the validated value of every visible field, in order. */
  onSubmit: (values: Array<{ node: FlatNode; value: unknown }>) => Promise<void>;
}

const COUNTRIES: Array<[string, string, string]> = [
  ["GB", "+44", "United Kingdom"],
  ["TR", "+90", "Türkiye"],
  ["IE", "+353", "Ireland"],
  ["US", "+1", "United States"],
  ["DE", "+49", "Germany"],
  ["FR", "+33", "France"],
  ["NL", "+31", "Netherlands"],
  ["ES", "+34", "Spain"],
  ["IT", "+39", "Italy"],
  ["AE", "+971", "United Arab Emirates"],
  ["SA", "+966", "Saudi Arabia"],
];

function initialDraft(
  nodes: FlatNode[],
  answers: Record<string, unknown>,
): Record<string, unknown> {
  const d: Record<string, unknown> = {};
  for (const n of nodes) {
    const v = answers[n.node.id];
    if (v !== undefined && v !== "") d[n.node.id] = v;
  }
  return d;
}

export function CardInput(p: CardInputProps) {
  const [draft, setDraft] = useState<Record<string, unknown>>(() =>
    initialDraft(cardNodes(p.form, p.stepId, p.answers as never), p.answers),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const merged = useMemo(() => ({ ...p.answers, ...draft }), [p.answers, draft]);
  const nodes = useMemo(
    () => cardNodes(p.form, p.stepId, merged as never),
    [p.form, p.stepId, merged],
  );
  const set = (id: string, v: unknown) => setDraft((d) => ({ ...d, [id]: v }));

  const submit = async (e: Event) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const out: Array<{ node: FlatNode; value: unknown }> = [];
    for (const n of nodes) {
      const raw = draft[n.node.id];
      const r = validateAnswer(n.node, raw === undefined ? "" : raw);
      if (!r.ok) errs[n.node.id] = r.code;
      else out.push({ node: n, value: r.value });
    }
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = Object.keys(errs)[0]!;
      (document.getElementById(`in-${first}`) as HTMLElement | null)?.focus();
      return;
    }
    await p.onSubmit(out);
  };

  return (
    <form class="in card-step" onSubmit={(e) => void submit(e)} aria-label={p.stepTitle}>
      {nodes.map((n) => (
        <Field
          key={n.node.id}
          node={n}
          value={draft[n.node.id]}
          error={errors[n.node.id]}
          locale={p.locale}
          onChange={(v) => set(n.node.id, v)}
        />
      ))}
      <div class="row">
        <button type="submit" class="primary" disabled={p.busy}>
          {t(p.locale, "chrome.continueStep")}
        </button>
      </div>
    </form>
  );
}

function Field({
  node,
  value,
  error,
  locale,
  onChange,
}: {
  node: FlatNode;
  value: unknown;
  error?: string;
  locale: string;
  onChange: (v: unknown) => void;
}) {
  const n = node.node;
  const id = `in-${n.id}`;
  const label = (
    <label class="fl" for={id}>
      {n.prompt}
      {!n.required && <span class="muted"> ({t(locale, "chrome.optional")})</span>}
    </label>
  );
  const err = error ? (
    <div class="err" role="alert">
      {t(locale, `validation.${error}` as never)}
    </div>
  ) : null;
  switch (n.type) {
    case "long_text":
      return (
        <div class="f">
          {label}
          <textarea
            id={id}
            rows={3}
            maxLength={n.maxLength ?? 800}
            value={typeof value === "string" ? value : ""}
            onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
          />
          {err}
        </div>
      );
    case "text":
    case "email":
    case "number":
    case "date":
      return (
        <div class="f">
          {label}
          <input
            id={id}
            type={
              n.type === "email"
                ? "email"
                : n.type === "date"
                  ? "date"
                  : n.type === "number"
                    ? "number"
                    : "text"
            }
            inputMode={n.type === "number" ? "numeric" : n.type === "email" ? "email" : "text"}
            autocomplete={
              n.maps === "contact.firstName"
                ? "given-name"
                : n.maps === "contact.lastName"
                  ? "family-name"
                  : n.maps === "contact.fullName"
                    ? "name"
                    : n.type === "email"
                      ? "email"
                      : n.maps === "contact.country"
                        ? "country-name"
                        : n.maps === "contact.city"
                          ? "address-level2"
                          : "off"
            }
            value={value === undefined || value === null ? "" : String(value)}
            onInput={(e) => onChange((e.target as HTMLInputElement).value)}
          />
          {err}
        </div>
      );
    case "phone": {
      const def = n.defaultCountry ?? "GB";
      const current = typeof value === "string" ? value : "";
      const cc = COUNTRIES.find((c) => current.startsWith(c[1]))?.[0] ?? def;
      const dial = COUNTRIES.find((c) => c[0] === cc)?.[1] ?? "+44";
      const local = current.startsWith(dial)
        ? current.slice(dial.length)
        : current.replace(/^\+\d+/, "");
      return (
        <div class="f">
          {label}
          <div class="phone">
            <select
              aria-label={t(locale, "chrome.selectCountry")}
              value={cc}
              onChange={(e) =>
                onChange(
                  (COUNTRIES.find((c) => c[0] === (e.target as HTMLSelectElement).value)?.[1] ??
                    "+44") + local,
                )
              }
            >
              {COUNTRIES.map(([code, d, name]) => (
                <option key={code} value={code}>
                  {name} {d}
                </option>
              ))}
            </select>
            <input
              id={id}
              type="tel"
              inputMode="tel"
              autocomplete="tel-national"
              value={local}
              onInput={(e) =>
                onChange(
                  dial +
                    (e.target as HTMLInputElement).value.replace(/[\s()-]/g, "").replace(/^0+/, ""),
                )
              }
            />
          </div>
          {err}
        </div>
      );
    }
    case "single_choice":
      return (
        <div class="f">
          {label}
          <select
            id={id}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
          >
            <option value="">–</option>
            {n.options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          {err}
        </div>
      );
    case "multi_choice": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      return (
        <fieldset class="f">
          <legend class="fl">{n.prompt}</legend>
          <div class="chips">
            {n.options.map((o) => (
              <label key={o.id} class={`chip${arr.includes(o.id) ? " on" : ""}`}>
                <input
                  type="checkbox"
                  checked={arr.includes(o.id)}
                  onChange={(e) =>
                    onChange(
                      (e.target as HTMLInputElement).checked
                        ? [...arr, o.id]
                        : arr.filter((x) => x !== o.id),
                    )
                  }
                />
                {o.label}
              </label>
            ))}
          </div>
          {err}
        </fieldset>
      );
    }
    default:
      return null;
  }
}
